import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import moment from "moment";
import { FiArrowLeft, FiPaperclip } from "react-icons/fi";
import Loader from "../../../components/loader/Loader";
import {
  EmptyState,
  StatusBadge,
} from "../../../components/dashboard/DashboardUI";
import {
  DocumentList,
  RequestSteps,
} from "../../../components/modals/RequestDetailModal";
import {
  assignProcedure,
  closeProcedure,
} from "../../../redux/actions/ProcedureActions";
import { uploadRequestGeneralDocuments } from "../../../redux/actions/DocumentActions";
import { requestTitle } from "../../../utils/requestDisplay";
import { detailsFor } from "../../../utils/requestDetails";

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

const confirm = (verb) =>
  Swal.fire({
    title: `Voulez-vous vraiment ${verb} cette démarche ?`,
    text: "Cette action est irréversible.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#051259",
    cancelButtonColor: "#c5cad8",
    confirmButtonText: "Confirmer",
    cancelButtonText: "Annuler",
  });

// Operator page of a formality request: details, take over, then close it
// with a comment and the resulting document
function OperatorRequestDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.authUser.user);
  const { procedure } = location.state || {};

  const [submitting, setSubmitting] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState(null);

  if (!procedure) {
    return (
      <div className="dash-card">
        <EmptyState title="Démarche introuvable">
          Revenez à la liste des démarches pour en ouvrir une.
        </EmptyState>
      </div>
    );
  }

  const { rows, documents } = detailsFor(procedure);
  const client = procedure.user || {};
  const canTake = procedure.status === "paid" && !procedure.operator;
  const canClose = procedure.status === "in_progress" && procedure.operator;

  const done = ({ success, message }) => {
    setSubmitting(false);
    if (!success) {
      toast("error", "Échec", message);
      return;
    }
    toast("success", "Succès", message);
    navigate(-1);
  };

  const takeOver = async () => {
    const { isConfirmed } = await confirm("traiter");
    if (!isConfirmed) return;

    setSubmitting(true);
    dispatch(assignProcedure(token, { request: procedure.reference }, done));
  };

  const closeRequest = async (event) => {
    event.preventDefault();
    const { isConfirmed } = await confirm("clôturer");
    if (!isConfirmed) return;

    setSubmitting(true);
    const formData = new FormData();
    formData.append("documents", attachment);

    dispatch(
      uploadRequestGeneralDocuments(
        token,
        procedure.reference,
        formData,
        ({ success, message }) => {
          if (!success) {
            done({ success, message });
            return;
          }
          dispatch(
            closeProcedure(
              token,
              {
                request: procedure.reference,
                status: "fulfilled",
                comment,
              },
              done,
            ),
          );
        },
      ),
    );
  };

  return (
    <>
      <Loader isLoading={submitting} />

      <div className="dash-page-header">
        <div>
          <button
            type="button"
            className="dash-back"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft aria-hidden="true" /> Démarches clients
          </button>
          <h1 className="dash-page-title">{requestTitle(procedure)}</h1>
          <p className="dash-page-lead">
            #{procedure.reference} ·{" "}
            {moment(procedure.createdAt).format("DD/MM/YYYY")}
          </p>
        </div>
        <div className="dash-header-actions">
          <StatusBadge status={procedure.status} />
          {canTake && (
            <button type="button" className="dash-button" onClick={takeOver}>
              Prendre en charge
            </button>
          )}
          {canClose && (
            <button
              type="button"
              className="dash-button"
              onClick={() => setShowClose(true)}
            >
              Clôturer
            </button>
          )}
        </div>
      </div>

      <div className="dash-detail-grid">
        <section className="dash-card">
          <RequestSteps status={procedure.status} />
          <h2 className="dash-card-title">Informations de la démarche</h2>
          {rows.length ? (
            <dl className="dash-details">
              {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd className="dash-pre">{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          ) : (
            <p className="dash-page-lead">Aucune information complémentaire.</p>
          )}
          <DocumentList documents={documents} />
          {procedure.comment && (
            <div className="dash-note">
              <h3 className="dash-subtitle">Commentaire de clôture</h3>
              <p className="dash-pre">{procedure.comment}</p>
            </div>
          )}
        </section>

        <aside className="dash-card">
          <h2 className="dash-card-title">Client</h2>
          <dl className="dash-details is-stacked">
            <dt>Nom</dt>
            <dd>
              {[client.firstname, client.lastname].filter(Boolean).join(" ") ||
                "—"}
            </dd>
            <dt>E-mail</dt>
            <dd>{client.email || "—"}</dd>
            <dt>Téléphone</dt>
            <dd>{client.mobile || "—"}</dd>
            <dt>WhatsApp</dt>
            <dd>{client.whatsApp || "—"}</dd>
          </dl>
        </aside>
      </div>

      <Modal show={showClose} onHide={() => setShowClose(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="dash-card-title mb-0">
            Clôturer la démarche
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="dash-form" onSubmit={closeRequest}>
            <label htmlFor="close-comment">Message au client</label>
            <textarea
              id="close-comment"
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Expliquez le résultat de la démarche"
              required
            />
            <label htmlFor="close-file" className="dash-file">
              <FiPaperclip aria-hidden="true" />
              {attachment
                ? attachment.name
                : "Joindre le document remis au client"}
            </label>
            <input
              id="close-file"
              type="file"
              className="visually-hidden"
              onChange={(event) => setAttachment(event.target.files?.[0])}
              required
            />
            <div className="dash-form-actions">
              <button
                type="button"
                className="dash-button is-secondary"
                onClick={() => setShowClose(false)}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="dash-button"
                disabled={!comment.trim() || !attachment}
              >
                Clôturer
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OperatorRequestDetails;
